import { useLayoutEffect, useRef, useState } from 'react'

import { computeTooltipPlacement } from '~/models/tooltipPlacement'

type Placement = { left: number; top: number }

type UseTooltipPlacementArgs = {
  /**
   * ツールチップを載せる要素 (= 下の `anchorOffset*` の原点)。
   * チャートライブラリが渡すのはプロット内の相対座標なので、viewport 基準の
   * 配置にはこの要素の矩形が要る。
   */
  anchorRef: React.RefObject<HTMLElement | null>
  /** anchor 左上からのカーソル相対 x。位置が未知なら undefined。 */
  anchorOffsetX: number | undefined
  /** anchor 左上からのカーソル相対 y。位置が未知なら undefined。 */
  anchorOffsetY: number | undefined
  /** カーソルとツールチップの間隔 (px)。 */
  cursorOffset: number
  /** ツールチップが開いているか (閉じている間は placement を捨てる)。 */
  isOpen: boolean
}

/**
 * ウィンドウ基準でツールチップを配置する ([[computeTooltipPlacement]] の適用側)。
 * 返す `tooltipRef` をツールチップ本体に付け、`placement` が null の間は
 * 透明にしておく (= 実寸を測るまで位置が決まらない 1 フレーム)。
 *
 * 実寸は毎回測り直す: 中身はホバー中のコマごとに増減するため、前回の寸法で
 * 反転を判定すると端で位置が飛ぶ。
 */
export function useTooltipPlacement({
  anchorRef,
  anchorOffsetX,
  anchorOffsetY,
  cursorOffset,
  isOpen,
}: UseTooltipPlacementArgs): {
  /** ツールチップ本体に付ける (実寸の測定元)。 */
  tooltipRef: React.RefObject<HTMLDivElement>
  placement: Placement | null
} {
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [placement, setPlacement] = useState<Placement | null>(null)
  // listener 側から最新の入力で測り直すための握り。deps に座標を入れて listener を
  // 張り直すと、mousemove のたびに ResizeObserver ごと作り直すことになる。
  const applyRef = useRef<() => void>(() => undefined)

  useLayoutEffect(() => {
    const apply = (): void => {
      const anchor = anchorRef.current
      const tooltip = tooltipRef.current
      if (
        !isOpen ||
        anchor == null ||
        tooltip == null ||
        anchorOffsetX == null ||
        anchorOffsetY == null
      ) {
        setPlacement(null)
        return
      }

      const anchorRect = anchor.getBoundingClientRect()
      const { width, height } = tooltip.getBoundingClientRect()
      const next = computeTooltipPlacement({
        cursor: {
          x: anchorRect.left + anchorOffsetX,
          y: anchorRect.top + anchorOffsetY,
        },
        tooltip: { width, height },
        // why not window.innerWidth / innerHeight: スクロールバーの幅を含むため、
        // 右端・下端でツールチップがバーの下に潜る。clientWidth / clientHeight は
        // バーを除いた実効の表示領域。
        viewport: {
          width: document.documentElement.clientWidth,
          height: document.documentElement.clientHeight,
        },
        offset: cursorOffset,
      })
      setPlacement((previous) =>
        previous != null &&
        previous.left === next.left &&
        previous.top === next.top
          ? previous
          : next,
      )
    }

    applyRef.current = apply
    apply()
  }, [anchorRef, anchorOffsetX, anchorOffsetY, cursorOffset, isOpen])

  useLayoutEffect(() => {
    if (!isOpen) return

    // 中身はホバー中のコマごとに増減する (系列の null / トグル OFF でバッジも
    // セクションも消える)。前回の寸法で反転を判定すると端で位置が飛ぶので、
    // 開いている間ずっと追従させる。
    //
    // why not ここで scroll / resize も拾う: それらは anchor だけが動きポインタは
    // 動かないため、相対座標が古いまま「古い相対座標 × 新しい矩形」を合成して
    // しまう。座標が古くなったかどうかの判定は呼び出し側 (isOpen を false にする)
    // が 1 箇所で持つ — ここに 2 つ目の判定を置くと、両者の前提がずれた時に
    // この経路だけが古い座標で置き直す。
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => applyRef.current())
    if (tooltipRef.current != null) observer?.observe(tooltipRef.current)

    return () => {
      observer?.disconnect()
    }
  }, [isOpen])

  return { tooltipRef, placement }
}
